import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import Disclaimer from '../components/Disclaimer';
import { getContributionLimit, ACCOUNT_TYPES, YEARS } from '../data/contributionLimits';

const fmt = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const ACCOUNT_LABELS = {
  'roth-ira': 'Roth IRA',
  'traditional-ira': 'Traditional IRA',
  '401k': '401(k)',
  '403b': '403(b)',
  'hsa': 'HSA',
  'fsa': 'FSA',
  'sep-ira': 'SEP-IRA',
};

export default function ContributionLimits() {
  const { account, year } = useParams();
  const data = getContributionLimit(account, year);

  if (!data) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>Account or year not found</h1>
          <p style={s.body}>We have contribution limit data for Roth IRA, Traditional IRA, 401(k), 403(b), HSA, FSA, and SEP-IRA for years 2022–2026.</p>
          <Link to="/contribution-limits/401k/2025" style={s.ctaBtn}>View 401(k) 2025 limits →</Link>
        </div>
      </div>
    );
  }

  const { yearData, displayName, emoji, description, faq: faqItems } = data;
  const yearNum = parseInt(year, 10);

  // Dynamic FAQs using the data's built-in FAQ items
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  // Build phaseout row if applicable
  const hasPhaseout = yearData.incomePhaseoutSingle || yearData.deductibilityPhaseoutSingle;
  const phaseoutLabel = yearData.incomePhaseoutSingle ? 'Income Phase-Out (Single)' : 'Deductibility Phase-Out (Single)';
  const phaseoutMFJLabel = yearData.incomePhaseoutMFJ ? 'Income Phase-Out (MFJ)' : 'Deductibility Phase-Out (MFJ)';
  const phaseoutSingleData = yearData.incomePhaseoutSingle || yearData.deductibilityPhaseoutSingle;
  const phaseoutMFJData = yearData.incomePhaseoutMFJ || yearData.deductibilityPhaseoutMFJ;

  // Related year links
  const otherYears = YEARS.filter(y => y !== yearNum); // eslint-disable-line no-unused-vars
  const otherAccounts = ACCOUNT_TYPES.filter(a => a !== account);

  const isHSA = account === 'hsa';
  const isFSA = account === 'fsa';
  const isSEP = account === 'sep-ira';

  return (
    <div style={s.page}>
      <SEO
        title={`${displayName} Contribution Limits ${yearNum} | KnowFi`}
        description={`${displayName} contribution limit for ${yearNum}: ${fmt(yearData.limit)}${yearData.catchUpLimit ? ` (${fmt(yearData.catchUpLimit)} with catch-up for age ${yearData.catchUpAge}+)` : ''}. See income phase-outs, rules, and how to maximize your contributions.`}
        path={`/contribution-limits/${account}/${yearNum}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadLink}>{displayName}</span>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>{yearNum}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.accountBadge}>{emoji} {displayName} · {yearNum}</div>
          <h1 style={s.h1}>{displayName} Contribution Limits for {yearNum}</h1>
          <p style={s.subtitle}>{description}</p>
        </div>

        {/* Hero stats */}
        <div style={s.heroGrid}>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>
              {isHSA ? 'Individual Limit' : isSEP ? 'Max Contribution' : 'Annual Limit'}
            </div>
            <div style={{ ...s.heroStat, color: '#38bdf8' }}>{fmt(yearData.limit)}</div>
            {isHSA && <div style={s.heroSub}>Individual coverage</div>}
            {isSEP && <div style={s.heroSub}>or 25% of compensation</div>}
            {!isHSA && !isSEP && <div style={s.heroSub}>per year</div>}
          </div>

          {isHSA && (
            <div style={s.heroCard}>
              <div style={s.heroLabel}>Family Limit</div>
              <div style={{ ...s.heroStat, color: '#a78bfa' }}>{fmt(yearData.familyLimit)}</div>
              <div style={s.heroSub}>family coverage</div>
            </div>
          )}

          {!isHSA && !isFSA && !isSEP && (
            <div style={s.heroCard}>
              <div style={s.heroLabel}>Catch-Up (age {yearData.catchUpAge}+)</div>
              <div style={{ ...s.heroStat, color: '#a78bfa' }}>{fmt(yearData.catchUpLimit)}</div>
              <div style={s.heroSub}>total with catch-up</div>
            </div>
          )}

          {isHSA && (
            <div style={s.heroCard}>
              <div style={s.heroLabel}>Catch-Up (age 55+)</div>
              <div style={{ ...s.heroStat, color: '#a78bfa' }}>{fmt(yearData.catchUpLimit)}</div>
              <div style={s.heroSub}>individual with catch-up</div>
            </div>
          )}

          {isFSA && (
            <div style={s.heroCard}>
              <div style={s.heroLabel}>Max Rollover</div>
              <div style={{ ...s.heroStat, color: '#22c55e' }}>{fmt(yearData.rolloverMax)}</div>
              <div style={s.heroSub}>carries to next year</div>
            </div>
          )}

          {hasPhaseout && phaseoutSingleData && (
            <div style={s.heroCard}>
              <div style={s.heroLabel}>Phase-Out Starts (Single)</div>
              <div style={{ ...s.heroStat, color: '#f59e0b' }}>{fmt(phaseoutSingleData.low)}</div>
              <div style={s.heroSub}>MAGI threshold</div>
            </div>
          )}
        </div>

        {/* Limit breakdown table */}
        <div style={s.sectionTitle}>Full Limit Details — {yearNum}</div>
        <div style={s.table}>

          {isHSA ? (
            <>
              <div style={s.tableRow}><div style={s.tableKey}>Individual contribution limit</div><div style={s.tableVal}>{fmt(yearData.limit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Family contribution limit</div><div style={s.tableVal}>{fmt(yearData.familyLimit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Catch-up contribution (age 55+)</div><div style={s.tableVal}>+{fmt(yearData.catchUpLimit - yearData.limit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Individual with catch-up (55+)</div><div style={s.tableVal}>{fmt(yearData.catchUpLimit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Family with catch-up (55+)</div><div style={s.tableVal}>{fmt(yearData.catchUpFamilyLimit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>HDHP min deductible (individual)</div><div style={s.tableVal}>{fmt(yearData.hdhpMinDeductible.individual)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>HDHP min deductible (family)</div><div style={s.tableVal}>{fmt(yearData.hdhpMinDeductible.family)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>HDHP max out-of-pocket (individual)</div><div style={s.tableVal}>{fmt(yearData.hdhpMaxOOP.individual)}</div></div>
              <div style={{ ...s.tableRow, borderBottom: 'none' }}><div style={s.tableKey}>HDHP max out-of-pocket (family)</div><div style={s.tableVal}>{fmt(yearData.hdhpMaxOOP.family)}</div></div>
            </>
          ) : isFSA ? (
            <>
              <div style={s.tableRow}><div style={s.tableKey}>Healthcare FSA limit</div><div style={s.tableVal}>{fmt(yearData.limit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Max rollover to next year</div><div style={s.tableVal}>{fmt(yearData.rolloverMax)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Grace period (alternative to rollover)</div><div style={s.tableVal}>{yearData.gracePerod}</div></div>
              <div style={{ ...s.tableRow, borderBottom: 'none' }}><div style={s.tableKey}>Dependent Care FSA limit</div><div style={s.tableVal}>{fmt(yearData.dependentCareLimit)}</div></div>
            </>
          ) : isSEP ? (
            <>
              <div style={s.tableRow}><div style={s.tableKey}>Maximum contribution limit</div><div style={s.tableVal}>{fmt(yearData.limit)}</div></div>
              <div style={s.tableRow}><div style={s.tableKey}>Percentage of compensation limit</div><div style={s.tableVal}>{yearData.percentageLimit}%</div></div>
              <div style={{ ...s.tableRow, borderBottom: 'none' }}><div style={s.tableKey}>Effective limit (lesser of dollar or %)</div><div style={s.tableVal}>Lesser applies</div></div>
            </>
          ) : (
            <>
              <div style={s.tableRow}><div style={s.tableKey}>Employee contribution limit</div><div style={s.tableVal}>{fmt(yearData.limit)}</div></div>
              {yearData.catchUpLimit && <div style={s.tableRow}><div style={s.tableKey}>Catch-up contribution (age {yearData.catchUpAge}+)</div><div style={s.tableVal}>+{fmt(yearData.catchUpLimit - yearData.limit)}</div></div>}
              {yearData.catchUpLimit && <div style={s.tableRow}><div style={s.tableKey}>Total with catch-up (age {yearData.catchUpAge}+)</div><div style={s.tableVal}>{fmt(yearData.catchUpLimit)}</div></div>}
              {yearData.superCatchUp && (
                <>
                  <div style={s.tableRow}><div style={s.tableKey}>Super catch-up (ages {yearData.superCatchUpAge})</div><div style={s.tableVal}>{fmt(yearData.superCatchUp)} total</div></div>
                </>
              )}
              {yearData.employerMax && <div style={s.tableRow}><div style={s.tableKey}>Combined employee+employer max</div><div style={s.tableVal}>{fmt(yearData.employerMax)}</div></div>}
              {yearData.special15YearCatchUp && <div style={s.tableRow}><div style={s.tableKey}>Special 15-year service catch-up</div><div style={s.tableVal}>+{fmt(yearData.special15YearCatchUp)}</div></div>}
              {hasPhaseout && phaseoutSingleData && (
                <>
                  <div style={s.tableRow}><div style={s.tableKey}>{phaseoutLabel}</div><div style={s.tableVal}>{fmt(phaseoutSingleData.low)} – {fmt(phaseoutSingleData.high)}</div></div>
                  {phaseoutMFJData && <div style={s.tableRow}><div style={s.tableKey}>{phaseoutMFJLabel}</div><div style={s.tableVal}>{fmt(phaseoutMFJData.low)} – {fmt(phaseoutMFJData.high)}</div></div>}
                </>
              )}
            </>
          )}
        </div>

        {/* Notes */}
        {yearData.notes && (
          <div style={s.notesBox}>
            <div style={s.notesLabel}>📌 Important Notes for {yearNum}</div>
            <p style={s.notesText}>{yearData.notes}</p>
          </div>
        )}

        {/* Year comparison */}
        <div style={s.sectionTitle}>{displayName} Limit History</div>
        <div style={s.table}>
          {YEARS.map((y, i) => {
            const yd = data.limits[y];
            if (!yd) return null;
            const isCurrentYear = y === yearNum;
            return (
              <div key={y} style={{ ...s.tableRow, ...(i === YEARS.length - 1 ? { borderBottom: 'none' } : {}), ...(isCurrentYear ? { background: 'rgba(56,189,248,0.06)' } : {}) }}>
                <div style={s.tableKey}>
                  <Link to={`/contribution-limits/${account}/${y}`} style={{ ...s.tableLink, ...(isCurrentYear ? { color: '#38bdf8' } : {}) }}>
                    {y}{isCurrentYear ? ' ← Current' : ''}
                  </Link>
                </div>
                <div style={s.tableVal}>
                  {fmt(yd.limit)}
                  {yd.catchUpLimit && <span style={s.tableSub}> / {fmt(yd.catchUpLimit)} w/catch-up</span>}
                  {yd.familyLimit && <span style={s.tableSub}> / {fmt(yd.familyLimit)} family</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ section */}
        <div style={s.sectionTitle}>Frequently Asked Questions</div>
        <div style={s.faqList}>
          {faqItems.map((item, i) => (
            <div key={i} style={s.faqItem}>
              <div style={s.faqQ}>{item.q}</div>
              <div style={s.faqA}>{item.a}</div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>💰 Want to maximize your contributions?</div>
          <div style={s.ctaRow}>
            <Link to="/howto" style={s.ctaBtn}>📚 How-To Guides</Link>
            <Link to="/calculators" style={s.ctaBtnSecondary}>🔢 Retirement Calculator</Link>
          </div>
        </div>

        {/* Other accounts */}
        <div style={s.sectionTitle}>Other Account Types</div>
        <div style={s.relatedGrid}>
          {otherAccounts.map(a => (
            <Link key={a} to={`/contribution-limits/${a}/${yearNum}`} style={s.relatedCard}>
              <div style={s.relatedLabel}>{ACCOUNT_LABELS[a]}</div>
              <div style={s.relatedSub}>{yearNum} limits →</div>
            </Link>
          ))}
        </div>

        <div style={s.exploreRow}>
          <div style={s.exploreLabel}>Explore More Data</div>
          <div style={s.exploreLinks}>
            <Link to="/savings-by-age/30" style={s.exploreLink}>📈 Savings by Age</Link>
            <Link to="/savings-by-state/texas" style={s.exploreLink}>🗺️ Savings by State</Link>
            <Link to="/market-returns/sp500/2024" style={s.exploreLink}>📊 Market Returns</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px', flexWrap: 'wrap' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:      { marginBottom: '32px' },
  accountBadge:{ display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:          { fontSize: 'clamp(22px, 4vw, 36px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle:    { fontSize: '16px', color: '#64748b', margin: 0, lineHeight: 1.65 },

  heroGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '12px', marginBottom: '28px' },
  heroCard: { background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155', textAlign: 'center' },
  heroLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' },
  heroStat: { fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: '900', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: '6px' },
  heroSub:  { fontSize: '12px', color: '#475569' },

  sectionTitle: { fontSize: '13px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', marginTop: '28px' },

  table:    { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '8px' },
  tableRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid #334155', gap: '12px' },
  tableKey: { fontSize: '13px', color: '#64748b', flex: 1 },
  tableVal: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', textAlign: 'right', whiteSpace: 'nowrap' },
  tableSub: { fontSize: '12px', color: '#475569', fontWeight: '400' },
  tableLink:{ color: '#64748b', textDecoration: 'none' },

  notesBox:  { background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '12px', padding: '16px 20px', marginBottom: '8px', marginTop: '8px' },
  notesLabel:{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  notesText: { fontSize: '14px', color: '#94a3b8', margin: 0, lineHeight: 1.7 },

  faqList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' },
  faqItem: { background: '#1e293b', borderRadius: '12px', padding: '18px 20px', border: '1px solid #334155' },
  faqQ:    { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '8px' },
  faqA:    { fontSize: '14px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  exploreRow:      { marginBottom: '28px', marginTop: '28px' },
  exploreLabel:    { fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  exploreLinks:    { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  exploreLink:     { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', fontSize: '13px', fontWeight: '600', padding: '8px 14px', borderRadius: '8px', textDecoration: 'none' },
  ctaSection:      { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '24px', marginTop: '32px', marginBottom: '28px', textAlign: 'center' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },

  body: { fontSize: '15px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  relatedGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '8px' },
  relatedCard:  { background: '#1e293b', borderRadius: '10px', padding: '14px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc' },
  relatedLabel: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
  relatedSub:   { fontSize: '12px', color: '#38bdf8' },
};
